import { FillScreen, HStack } from "@/components/Layout";
import { ViewportLoader } from "@/components/Loading";
import type { ValueOrUpdater } from "@dresscode-frontend/utility-types";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useEffect } from "react";
import { useListOperationTaskInstances } from "./-components/BulkAppUserOperation/-query";
import { SearchSchema } from "./-components/BulkAppUserOperation/-zod-schemas";
import { usePackage } from "./-hooks/usePackage";
import type { SearchParams } from "./-zod-schemas";

export const Route = createFileRoute(
  "/_staff/it-operation-core/_detail/operation-instances/$instanceId/_layout/data/",
)({
  component: RouteComponent,
  validateSearch: zodValidator(SearchSchema),
});

const parentRoute = getRouteApi(
  "/_staff/it-operation-core/_detail/operation-instances/$instanceId/_layout",
);

function RouteComponent() {
  const { id, packageType } = parentRoute.useLoaderData();
  const { Component } = usePackage({ packageType });

  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();

  // タスクインスタンスを取得
  const { data: taskInstances } = useListOperationTaskInstances(id);

  const onSearch = useCallback(
    (query: ValueOrUpdater<Partial<SearchParams>>) => {
      navigate({
        search: (prev) => {
          const newQuery = typeof query === "function" ? query(prev) : query;
          return { ...prev, ...newQuery };
        },
      });
    },
    [navigate],
  );

  // 初回アクセス時にoperationTaskInstanceIdが未設定の場合、デフォルト値を設定
  useEffect(() => {
    if (
      !searchParams.operationTaskInstanceId &&
      taskInstances &&
      taskInstances.length > 0
    ) {
      // InProgressタスクを優先、なければ最初のタスク
      const inProgressTask = taskInstances.find(
        (task) => task.status === "InProgress",
      );
      const defaultTaskId =
        inProgressTask?.taskInstanceId || taskInstances[0]?.taskInstanceId;

      if (defaultTaskId) {
        navigate({
          search: (prev) => ({
            ...prev,
            operationTaskInstanceId: defaultTaskId,
          }),
          replace: true, // 履歴を置き換える
        });
      }
    }
  }, [searchParams.operationTaskInstanceId, taskInstances, navigate]);

  // operationTaskInstanceIdが未設定かつタスクインスタンスを取得中の場合は、ローディング表示
  if (!searchParams.operationTaskInstanceId && !taskInstances) {
    return <ViewportLoader />;
  }

  return (
    <FillScreen>
      <HStack className="h-full overflow-hidden">
        <Component
          instanceId={id}
          searchParams={searchParams}
          onSearch={onSearch}
        />
      </HStack>
    </FillScreen>
  );
}
